import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import { isAdminOrSuperAdmin, isDirectorOrAdmin } from '../utils/auth';
import '../App.css';

const CreateTeam: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Only admins and directors can create teams
    if (user && !isDirectorOrAdmin(user.profileType)) {
      navigate('/teams');
    }
  }, [user, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    teamNumber: '',
    logo: '',
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    clubId: '',
    leagueId: '',
    teamFee: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState<Array<{ id: string; name: string }>>([]);
  const [leagues, setLeagues] = useState<Array<{ id: string; name: string }>>([]);

  const isDirector = user?.profileType === 'director';

  useEffect(() => {
    fetchClubs();
    fetchLeagues();
  }, []);

  const fetchClubs = async () => {
    try {
      // Directors only see their own clubs
      if (isDirector) {
        const response = await api.get('/clubs/my-clubs');
        const clubsData = response.data.clubs || response.data;
        setClubs(clubsData.map((c: any) => ({ id: c.id, label: c.name })));
      } else {
        const response = await api.get('/clubs', { params: { page: '1', limit: '100' } });
        const clubsData = response.data.clubs || response.data;
        setClubs(clubsData.map((c: any) => ({ id: c.id, label: c.name })));
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues', { params: { page: '1', limit: '100' } });
      const leaguesData = response.data.leagues || response.data;
      setLeagues(leaguesData.map((l: any) => ({ id: l.id, label: l.name })));
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Directors must select a club
    if (isDirector && !formData.clubId) {
      setError('You must select a club for this team');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/teams', {
        name: formData.name,
        teamNumber: formData.teamNumber,
        logo: formData.logo || undefined,
        colors: {
          primary: formData.primaryColor,
          secondary: formData.secondaryColor
        },
        clubId: formData.clubId || undefined,
        leagueId: formData.leagueId || undefined,
        teamFee: formData.teamFee ? parseFloat(formData.teamFee) : undefined
      });

      // Team created successfully, go to team management
      navigate(`/teams/${response.data.id}/manage`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '600px', marginTop: '50px' }}>
      <div className="card">
        <h1>Create New Team</h1>
        <p style={{ color: '#757575', marginBottom: '20px' }}>
          Create a new team. Teams are automatically approved.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Team Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Thunder FC"
            />
          </div>

          <div className="form-group">
            <label>Team Number *</label>
            <input
              type="text"
              name="teamNumber"
              value={formData.teamNumber}
              onChange={handleChange}
              required
              placeholder="e.g., TFC-001"
            />
          </div>

          <div className="form-group">
            <label>Logo URL (optional)</label>
            <input
              type="url"
              name="logo"
              value={formData.logo}
              onChange={handleChange}
              placeholder="https://example.com/logo.png"
            />
            <small style={{ color: '#757575', fontSize: '12px' }}>Enter a URL to your team logo image</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Primary Color *</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="color"
                  name="primaryColor"
                  value={formData.primaryColor}
                  onChange={handleChange}
                  style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  style={{ flex: 1 }}
                  placeholder="#000000"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Secondary Color *</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="color"
                  name="secondaryColor"
                  value={formData.secondaryColor}
                  onChange={handleChange}
                  style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                  style={{ flex: 1 }}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '20px', marginBottom: '20px', padding: '15px', background: '#F5F5F5', borderRadius: '8px' }}>
            <p style={{ marginBottom: '10px', fontWeight: 'bold' }}>Team Colors Preview:</p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '12px',
                  backgroundColor: formData.primaryColor,
                  border: `4px solid ${formData.secondaryColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: formData.secondaryColor,
                  fontWeight: 'bold'
                }}
              >
                {formData.name || 'Team'}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '14px' }}><strong>Primary:</strong> {formData.primaryColor}</p>
                <p style={{ margin: 0, fontSize: '14px' }}><strong>Secondary:</strong> {formData.secondaryColor}</p>
              </div>
            </div>
          </div>

          <SearchableDropdown
            label={isDirector ? 'Club *' : 'Club (optional)'}
            options={clubs.map(club => ({
              id: club.id,
              label: club.name,
              subtitle: `ID: ${club.id}`
            }))}
            value={formData.clubId}
            onChange={(value) => setFormData({ ...formData, clubId: value })}
            placeholder="Search and select a club..."
            allowClear={!isDirector}
          />

          <SearchableDropdown
            label="League (optional)"
            options={leagues.map(league => ({
              id: league.id,
              label: league.name,
              subtitle: `ID: ${league.id}`
            }))}
            value={formData.leagueId}
            onChange={(value) => setFormData({ ...formData, leagueId: value })}
            placeholder="Search and select a league..."
            allowClear={true}
          />

          <div className="form-group">
            <label>Team Fee Override ($)</label>
            <input
              type="number"
              name="teamFee"
              value={formData.teamFee}
              onChange={handleChange}
              placeholder="Leave blank to use club default"
              step="0.01"
              min="0"
            />
            <small style={{ color: '#757575', fontSize: '12px' }}>Optional: overrides the club's default team fee for this team</small>
          </div>

          {error && <div className="error">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Team'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/teams')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTeam;
